import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Trash2 } from 'lucide-react';
import { useNotify, useRecordContext, useUpdate } from 'ra-core';
import { useState } from 'react';
import { useHasPermission } from '../rbac/useHasPermission';

export const BlacklistVendorButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [update, { isPending }] = useUpdate();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const { hasPermission: canManage } = useHasPermission('manage_procurement');

    if (!record || record.status === 'blacklisted' || !canManage) return null;

    const handleBlacklist = () => {
        update(
            'vendors',
            {
                id: record.id,
                data: { status: 'blacklisted', blacklist_reason: reason || null },
                previousData: record,
            },
            {
                onSuccess: () => {
                    notify('Vendor Blacklisted', { type: 'success' });
                    setOpen(false);
                },
                onError: (error: any) => {
                    notify(`Error blacklisting vendor: ${error.message}`, { type: 'error' });
                },
            }
        );
    };

    return (
        <>
            <Button
                onClick={() => setOpen(true)}
                disabled={isPending}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 gap-2"
            >
                <Trash2 className="w-4 h-4" />
                Blacklist Vendor
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Blacklist Vendor</DialogTitle>
                        <DialogDescription>
                            <span className="font-semibold">{record.name}</span> will no longer be available for new RFQs.
                            You may optionally record a reason.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-2 py-2">
                        <Label htmlFor="blacklist_reason">Reason (optional)</Label>
                        <Textarea
                            id="blacklist_reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder="e.g. Repeated quality failures, compliance issues…"
                        />
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={isPending}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleBlacklist}
                            disabled={isPending}
                            className="bg-red-600 hover:bg-red-700 text-white gap-2"
                        >
                            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            Confirm Blacklist
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
